import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import GoogleButton from './GoogleButton/GoogleButton';
import MicrosoftButton from './MicrosoftButton/MicrosoftButton';
import { ReactComponent as Betterflies1 } from '../../../../../assets/icons/v3/betterflies-1.svg';
import { ReactComponent as Betterflies10 } from '../../../../../assets/icons/v3/betterflies-10.svg';
import { ReactComponent as Betterflies3 } from '../../../../../assets/icons/v3/betterflies-3.svg';
import BetterflyLogo from '../../../../../assets/icons/v3/betterfly-logo.svg?url';
import { ReactComponent as RightArrow } from '../../../../../assets/icons/v3/right-arrow.svg';
import BdsInput from '../../../../../shared/components/v3/BdsInput/BdsInput';
import BdsDivider from '../../../../../shared/components/v3/Globals/BdsDivider/BdsDivider';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { SIGN_UP_ROUTE } from '../../../../../shared/constants/auth';
import {
  ASSOCIATED_METHOD,
  CLICK_ACTION,
  ENTER_EMAIL,
  MANUAL_METHOD
} from '../../../../../shared/constants/tagManager';
import { LOGIN_ERROR_TOAST } from '../../../../../shared/constants/toast';
import { SessionContext } from '../../../../../shared/context/auth/authContext';
import useQueryParams from '../../../../../shared/hooks/useQueryParams/useQueryParams';
import { ILoginQueryParams } from '../../../../../shared/interfaces/auth';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../../shared/translations/auth';
import { GenericAsyncVoidFunction } from '../../../../../shared/types/genericTypes';
import { validateEmail } from '../../../../../shared/utils/validation/validation';
import './LoginCard.scss';

interface LoginCardProps {
  changeEmail: GenericAsyncVoidFunction;
}

const LoginCard = ({ changeEmail }: LoginCardProps) => {
  const { provider, email, verificationCode } = useQueryParams<ILoginQueryParams>();
  const [userEmail, setUserEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errorCode } = useContext(SessionContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const isValidEmail = useMemo(() => {
    return validateEmail(userEmail);
  }, [userEmail]);

  const handleChangeEmail = (email: string) => {
    setUserEmail(email);
    setEmailError(false);
  };

  const handleNextStep = async () => {
    if (isSubmitting) return;

    const validEmail = validateEmail(userEmail);
    setEmailError(!validEmail);

    if (validEmail) {
      setIsSubmitting(true);
      try {
        TagManagerService.pushEvent(CLICK_ACTION, ENTER_EMAIL, MANUAL_METHOD);
        await changeEmail(userEmail);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleValidation = (value: string | null) => {
    if (!value) {
      return null;
    }
    return emailError ? t(TRANSLATIONS_AUTH.loginCard.emailError) : null;
  };

  const handleKeyPress = () => {
    handleNextStep();
  };

  const handleCreatePasswordClick = () => {
    navigate(SIGN_UP_ROUTE, { state: { email, verificationCode } });
  };

  const renderButtons = () => {
    if (provider === 'google') {
      return <GoogleButton gtmTag={ASSOCIATED_METHOD} />;
    } else if (provider === 'microsoft') {
      return <MicrosoftButton gtmTag={ASSOCIATED_METHOD} />;
    }
    return (
      <>
        <GoogleButton gtmTag={ENTER_EMAIL} />
        <MicrosoftButton gtmTag={ENTER_EMAIL} />
      </>
    );
  };

  const handleCloseDeleteToast = () => {
    setErrorToast(false);
  };

  useEffect(() => {
    if (errorCode) {
      setErrorToast(true);
    }
  }, [errorCode]);

  return (
    <div className="login-card-container" data-experience="new">
      <img src={BetterflyLogo} className="btf-icon" alt="Betterfly icon" />

      <div className="login-card">
        <div className="top-right-images">
          <Betterflies1 className="image-1" />
          <Betterflies10 className="image-2" />
          <Betterflies3 className="image-3" />
        </div>

        <div className="login-text-container">
          <div className="login-title">{t(TRANSLATIONS_AUTH.loginCard.title)}</div>
          {provider && <div className="login-subtitle">{t(TRANSLATIONS_AUTH.loginCard.subtitle)}</div>}
        </div>
        <div className="login-buttons">{renderButtons()}</div>
        {!provider && (
          <>
            <div className="or-container">
              <BdsDivider size="s" color="light" />
              <div className="or-text">{t(TRANSLATIONS_AUTH.loginCard.or)}</div>
              <BdsDivider size="s" color="light" />
            </div>

            <BdsInput
              hintText={t(TRANSLATIONS_AUTH.loginCard.accessWithCredentials)}
              onChanged={handleChangeEmail}
              validator={handleValidation}
              onSubmitPressed={handleKeyPress}
              suffixIcon={<RightArrow className={`input-icon ${isValidEmail ? 'valid' : ''}`} />}
              onSuffixIconClick={handleNextStep}
              enabled={!isSubmitting}
              type="email"
              autoComplete="email"
            />
          </>
        )}
        {provider && (
          <div className="create-password-link">
            <Trans
              i18nKey={TRANSLATIONS_AUTH.loginCard.createPassword}
              components={{
                u: <button className="link" onClick={handleCreatePasswordClick} />
              }}
            />
          </div>
        )}
      </div>
      <Toast
        title={t(LOGIN_ERROR_TOAST.title)}
        description={t(LOGIN_ERROR_TOAST.description)}
        icon={LOGIN_ERROR_TOAST.icon}
        open={errorToast}
        button={LOGIN_ERROR_TOAST.button}
        buttonAction={handleCloseDeleteToast}
        handleClose={handleCloseDeleteToast}
      />
    </div>
  );
};

export default LoginCard;
